<template>
  <MainDialog :on-close="closeDialog" :show="show" :persistent="false">
    <v-card light class="pv-dialog">
      <v-card-title class="pv-dialog__title">
        <div>
          <div class="headline">{{ !printVariant ? "Print-Variante anlegen" : printVariantCopy.name || "Print-Variante bearbeiten" }}</div>
          <div class="text-caption text--secondary" v-if="printVariant">Print-Variante bearbeiten</div>
        </div>
      </v-card-title>

      <v-card-text>
        <!-- ======================= Allgemein ======================= -->
        <section class="pv-section">
          <div class="pv-section__head">
            <v-icon small class="mr-2">mdi-shape-outline</v-icon>
            <span>Allgemein</span>
          </div>
          <v-row dense>
            <v-col cols="8">
              <v-text-field v-model="printVariantCopy.name" label="Name" outlined dense hide-details />
            </v-col>
            <v-col cols="4">
              <v-autocomplete
                v-model="printVariantCopy.aspectRatio"
                label="Seitenverhältnis"
                :items="aspectRatios"
                outlined dense hide-details
              />
            </v-col>
            <v-col cols="6" class="mt-2">
              <div class="text-caption text--secondary mb-1">Portrait Mockup</div>
              <MockupSelector
                :aspect-ratio="printVariantCopy.aspectRatio"
                orientation="portrait"
                :selectedMockupId="printVariantCopy.mockupPortrait"
                @select="(id) => onSelectMockup(id, 'portrait')"
              />
            </v-col>
            <v-col cols="6" class="mt-2">
              <div class="text-caption text--secondary mb-1">Landscape Mockup</div>
              <MockupSelector
                :aspect-ratio="printVariantCopy.aspectRatio"
                orientation="landscape"
                :selectedMockupId="printVariantCopy.mockupLandscape"
                @select="(id) => onSelectMockup(id, 'landscape')"
              />
            </v-col>
          </v-row>
        </section>

        <!-- ======================= Preise ======================= -->
        <section class="pv-section">
          <div class="pv-section__head">
            <v-icon small class="mr-2">mdi-currency-eur</v-icon>
            <span>Preise</span>
          </div>

          <div class="pv-subhead">Produktion &amp; Express</div>
          <div class="pv-table-wrap">
            <table class="pv-table">
              <thead>
                <tr>
                  <th class="text-left">Fulfiller</th>
                  <th>Produktion</th>
                  <th>Express</th>
                  <th>Express-Aufpreis</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="fk in configuredFulfiller" :key="fk">
                  <td class="pv-table__label">{{ fulfillerName(fk) }}</td>
                  <td class="pv-table__input">
                    <v-text-field
                      dense hide-details solo flat background-color="#f4f5f7"
                      type="number" step="0.01" suffix="€"
                      v-model.number="printVariantCopy.fulfiller[fk].costs"
                    />
                  </td>
                  <td class="text-center">
                    <v-switch
                      dense inset hide-details class="pv-switch"
                      :input-value="expressAvailable(fk)"
                      @change="(v) => setExpressAvailable(fk, v)"
                    />
                  </td>
                  <td class="pv-table__input">
                    <v-text-field
                      v-if="expressAvailable(fk)"
                      dense hide-details solo flat background-color="#f4f5f7"
                      type="number" step="0.01" suffix="€"
                      v-model.number="printVariantCopy.fulfiller[fk].express.costs"
                    />
                    <span v-else class="pv-muted">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Verkaufspreise: Shopify-Listenpreis je Shop + manuelle Überschreibung je Land.
               Liegt in settings/printVariantPrices (eigenes Dokument) — printVariants bleibt unverändert. -->
          <div v-if="printVariant && printVariant.key" class="pv-subhead mt-5">
            Verkaufspreise
            <span class="pv-subhead__hint">Shopify-Listenpreis je Shop, darunter je Land überschreibbar · eigenes Dokument, nicht Teil von „Speichern"</span>
            <v-spacer />
            <v-btn small depressed rounded :loading="savingPrices" :disabled="!pricesDirty" @click="savePrices">Preise speichern</v-btn>
          </div>
          <div v-if="printVariant && printVariant.key" class="pv-table-wrap pv-prices">
            <div class="pv-prices__shops">
              <span v-for="(p, shopKey) in shopifyPrices" :key="shopKey" class="pv-prices__shop" :title="'SKU ' + p.sku + ' · ' + new Date(p.syncedAt).toLocaleString('de-DE')">
                <b>{{ shopKey }}</b> {{ Number(p.price).toFixed(2) }} {{ p.currency }}
              </span>
              <span v-if="!Object.keys(shopifyPrices).length" class="pv-muted">Noch kein Shopify-Preis geladen — oben auf der Karte „Kosten &amp; Gewinn" → „Preise laden"</span>
            </div>
            <table class="pv-table">
              <thead>
                <tr>
                  <th class="text-left">Land</th>
                  <th>Shop-Preis</th>
                  <th>Manuell (brutto, €)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="cc in priceCountries" :key="'price-' + cc">
                  <td class="pv-table__label">{{ cc }}</td>
                  <td class="text-center">
                    <span v-if="shopPriceFor(cc)">{{ shopPriceFor(cc).price.toFixed(2) }} {{ shopPriceFor(cc).currency }} <span class="pv-muted">{{ shopPriceFor(cc).label }}</span></span>
                    <span v-else class="pv-muted">—</span>
                  </td>
                  <td class="pv-table__input">
                    <v-text-field dense hide-details solo flat background-color="#f4f5f7" type="number" step="0.01" suffix="€" placeholder="wie Shop" v-model.number="manualPrices[cc]" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="pv-subhead mt-5">
            Versand je Land
            <span class="pv-subhead__hint">Fallback greift für jedes Land ohne eigenen Eintrag</span>
            <span class="pv-legend">
              <span class="pv-legend__dot pv-legend__dot--best" /> aktiv, günstigste Gesamtkosten
              <span class="pv-legend__dot pv-legend__dot--pricier ml-2" /> aktiv, Alternative wäre gesamt günstiger
            </span>
            <v-spacer />
            <v-switch
              dense inset hide-details class="pv-switch pv-totals-toggle"
              v-model="showTotals"
              label="Gesamtkosten anzeigen"
            />
          </div>
          <div class="pv-table-wrap">
            <table class="pv-table">
              <thead>
                <tr>
                  <th class="text-left">Land</th>
                  <template v-for="fk in configuredFulfiller">
                    <th :key="fk">{{ fulfillerName(fk) }}</th>
                    <th v-if="expressAvailable(fk)" :key="fk + '-x'">
                      {{ fulfillerName(fk) }} <span class="pv-muted">Express</span>
                    </th>
                  </template>
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr v-for="cc in shippingRows" :key="cc" :class="{ 'pv-fallback': cc === 'default' }">
                  <td class="pv-table__label">
                    <template v-if="cc === 'default'">
                      <v-icon x-small class="mr-1">mdi-earth</v-icon>Fallback
                    </template>
                    <template v-else>{{ flag(cc) }} {{ cc }}</template>
                  </td>
                  <template v-for="fk in configuredFulfiller">
                    <td :key="fk" class="pv-table__input" :class="cellClass(fk, 'standard', cc)"
                        :title="cellTitle(fk, 'standard', cc)">
                      <v-text-field
                        dense hide-details solo flat background-color="transparent"
                        type="number" step="0.01"
                        :value="printVariantCopy.fulfiller[fk].shippingCosts[cc]"
                        placeholder="–"
                        @input="(v) => setShipping(fk, 'standard', cc, v)"
                      />
                      <div v-if="showTotals" class="pv-total">
                        Σ {{ formatPrice(totalCosts(fk, cc)) }}
                      </div>
                    </td>
                    <td v-if="expressAvailable(fk)" :key="fk + '-x'" class="pv-table__input" :class="cellClass(fk, 'express', cc)"
                        :title="cellTitle(fk, 'express', cc)">
                      <v-text-field
                        dense hide-details solo flat background-color="transparent"
                        type="number" step="0.01"
                        :value="printVariantCopy.fulfiller[fk].express.shipping[cc]"
                        placeholder="–"
                        @input="(v) => setShipping(fk, 'express', cc, v)"
                      />
                    </td>
                  </template>
                  <td class="text-center" style="width: 40px">
                    <v-btn v-if="cc !== 'default'" icon x-small @click="removeShippingCountry(cc)">
                      <v-icon x-small>mdi-close</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="pv-add-row">
            <v-select
              dense hide-details outlined
              class="pv-add-row__select"
              label="Land hinzufügen"
              :items="availableShippingCountries"
              v-model="newShippingCountry"
            />
            <v-btn small depressed rounded color="primary" :disabled="!newShippingCountry" @click="addShippingCountry">
              <v-icon small left>mdi-plus</v-icon>Hinzufügen
            </v-btn>
          </div>
        </section>

        <!-- ======================= Fulfillment ======================= -->
        <section class="pv-section">
          <div class="pv-section__head">
            <v-icon small class="mr-2">mdi-truck-fast-outline</v-icon>
            <span>Fulfillment-Reihenfolge</span>
          </div>
          <div class="text-caption text--secondary mb-3">
            Der erste Eintrag ist der Default-Fulfiller. „Default" gilt für alle
            Länder ohne eigene Regel — Länder-Regeln nur anlegen, wo es abweicht.
          </div>

          <div
            v-for="cc in rankRows"
            :key="cc"
            class="pv-rank"
            :class="{ 'pv-rank--default': cc === 'default' }"
          >
            <span class="pv-rank__country">
              <template v-if="cc === 'default'">
                <v-icon x-small class="mr-1">mdi-earth</v-icon>Default
              </template>
              <template v-else>{{ flag(cc) }} {{ cc }}</template>
            </span>
            <transition-group name="pv-chip" tag="span" class="pv-rank__chips">
              <span v-for="(fk, index) in printVariantCopy.fulfillRank[cc]" :key="fk" class="pv-chip">
                <span class="pv-chip__rank">{{ index + 1 }}</span>
                {{ fulfillerName(fk) }}
                <v-icon v-if="index > 0" x-small class="pv-chip__btn" @click="moveRank(cc, index)">mdi-chevron-left</v-icon>
                <v-icon x-small class="pv-chip__btn" @click="removeRank(cc, index)">mdi-close</v-icon>
              </span>
            </transition-group>
            <v-menu v-if="rankCandidates(cc).length" offset-y>
              <template v-slot:activator="{ on }">
                <v-btn icon x-small v-on="on" class="pv-rank__add"><v-icon small>mdi-plus</v-icon></v-btn>
              </template>
              <v-list dense>
                <v-list-item v-for="fk in rankCandidates(cc)" :key="fk" @click="appendRank(cc, fk)">
                  <v-list-item-title>{{ fulfillerName(fk) }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-spacer />
            <v-btn v-if="cc !== 'default'" icon x-small @click="removeRankCountry(cc)">
              <v-icon x-small>mdi-delete-outline</v-icon>
            </v-btn>
          </div>

          <div class="pv-add-row">
            <v-select
              dense hide-details outlined
              class="pv-add-row__select"
              label="Länder-Regel hinzufügen"
              :items="availableRankCountries"
              v-model="newRankCountry"
            />
            <v-btn small depressed rounded color="primary" :disabled="!newRankCountry" @click="addRankCountry">
              <v-icon small left>mdi-plus</v-icon>Hinzufügen
            </v-btn>
          </div>
        </section>

        <!-- ======================= Producer ======================= -->
        <section class="pv-section pv-section--collapsible">
          <div class="pv-section__head pv-section__head--toggle" @click="showProducer = !showProducer">
            <v-icon small class="mr-2">mdi-cog-outline</v-icon>
            <span>Producer-Einstellungen</span>
            <span class="pv-subhead__hint ml-2">technische Felder</span>
            <v-spacer />
            <v-icon small>{{ showProducer ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-show="showProducer">
              <div v-for="fk in configuredFulfiller" :key="fk" class="pv-producer">
                <div class="pv-producer__name">{{ fulfillerName(fk) }}</div>
                <v-row dense>
                  <v-col cols="6" v-for="fieldKey in apiFieldKeys(fk)" :key="fieldKey">
                    <v-text-field
                      v-if="typeof requiredFieldsByFulfiller[fk][fieldKey] === 'number'"
                      dense outlined hide-details
                      :rules="numberRules"
                      v-model.number="printVariantCopy.fulfiller[fk][fieldKey]"
                      :label="fieldKey"
                    />
                    <v-text-field
                      v-else
                      dense outlined hide-details
                      v-model="printVariantCopy.fulfiller[fk][fieldKey]"
                      :label="fieldKey"
                    />
                  </v-col>
                </v-row>
              </div>
              <div v-if="availableFulfiller.length" class="pv-add-row">
                <v-select
                  :items="availableFulfiller"
                  item-text="name"
                  item-value="key"
                  dense hide-details outlined
                  class="pv-add-row__select"
                  label="Fulfiller hinzufügen"
                  v-model="selectedFulfiller"
                />
                <v-btn small depressed rounded color="primary" :disabled="!selectedFulfiller" @click="addFulfiller">
                  <v-icon small left>mdi-plus</v-icon>Hinzufügen
                </v-btn>
              </div>
            </div>
          </v-expand-transition>
        </section>
      </v-card-text>

      <v-card-actions class="pv-actions">
        <v-btn
          v-if="printVariant"
          text
          rounded
          :color="printVariant.isArchived ? 'success' : 'warning'"
          :disabled="isSaving"
          @click="toggleArchived"
        >
          <v-icon small left>{{ printVariant.isArchived ? "mdi-archive-arrow-up" : "mdi-archive-arrow-down" }}</v-icon>
          {{ printVariant.isArchived ? "Wiederherstellen" : "Archivieren" }}
        </v-btn>
        <v-spacer />
        <v-btn :disabled="isSaving" text rounded @click="closeDialog">Abbrechen</v-btn>
        <v-btn color="primary" depressed rounded large :loading="isSaving" @click="save">
          Speichern
        </v-btn>
      </v-card-actions>
    </v-card>
  </MainDialog>
</template>

<script>
import MainDialog from "@/components/MainDialog";
import { mapActions } from "vuex";
import { countryCodes } from "@/constants";
import { availableAspectRatios } from "@/config";
import MockupSelector from "@/components/Settings/Mockups/MockupSelector";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../../../../fb";
import { BASE_COUNTRIES as PRICE_COUNTRIES, mergeConfig, shopFor } from "@/components/Settings/Products/CostProfit/pricing";

const _ = require("lodash");

// Nicht als Producer-API-Felder anzuzeigende Kernfelder
const CORE_FIELDS = ["costs", "shippingCosts", "express"];

export default {
  name: "PrintVariantDialog",
  components: { MockupSelector, MainDialog },
  data() {
    return {
      // Verkaufspreise (settings/printVariantPrices) — unabhängig von printVariantCopy
      priceCountries: PRICE_COUNTRIES,
      priceConfig: mergeConfig(null),
      shopifyPrices: {},
      countryPrices: {},
      marketPrices: {},
      manualPrices: {},
      manualPricesSaved: "{}",
      savingPrices: false,
      isSaving: false,
      showProducer: false,
      showTotals: false,
      aspectRatios: availableAspectRatios,
      selectedFulfiller: "",
      newShippingCountry: "",
      newRankCountry: "",
      allFulfiller: [
        {
          key: "picanova",
          name: "Picanova",
          requiredFields: {
            costs: 0,
            shippingCosts: {},
            variant_code: "",
            options: JSON.stringify({ 1: 1, 2: 6 }),
          },
        },
        {
          key: "picanovaUS",
          name: "merchOne US",
          requiredFields: {
            costs: 0,
            shippingCosts: {},
            variant_code: "",
            options: JSON.stringify({ 1: 1, 2: 6 }),
          },
        },
        {
          key: "myPoster",
          name: "MyPoster",
          requiredFields: {
            costs: 0,
            shippingCosts: {},
            material: "",
            options: JSON.stringify([]),
            width: 0,
            height: 0,
            express: { isAvailable: false, costs: 0, shipping: {} },
          },
        },
        {
          key: "doboxa",
          name: "Doboxa",
          requiredFields: {
            costs: 0,
            shippingCosts: {},
            doboxa_product_id: "",
            options: JSON.stringify([]),
            width: 0,
            height: 0,
            express: { isAvailable: false, costs: 0, shipping: {} },
          },
        },
        {
          key: "manual",
          name: "Manuell",
          requiredFields: {
            costs: 0,
            shippingCosts: {},
            express: { isAvailable: false, costs: 0, shipping: {} },
          },
        },
      ],
      defaultPrintVariant: {
        fulfiller: {},
        fulfillRank: {},
        name: "",
      },
      printVariantCopy: {},
      numberRules: [
        (value) =>
          /^\d+(\.\d{1,2})?$/.test(value) ||
          "Zahl mit maximal 2 Nachkommastellen",
      ],
    };
  },
  props: {
    show: { default: false },
    onClose: { required: true },
    printVariant: { type: Object, required: false },
  },
  computed: {
    pricesDirty() {
      return JSON.stringify(this.manualPrices) !== this.manualPricesSaved;
    },
    configuredFulfiller() {
      return Object.keys(this.printVariantCopy?.fulfiller || {});
    },
    availableFulfiller() {
      return this.allFulfiller.filter(
        (fulfiller) => !this.printVariantCopy?.fulfiller?.[fulfiller.key]
      );
    },
    requiredFieldsByFulfiller() {
      return this.allFulfiller.reduce((obj, item) => {
        obj[item.key] = item.requiredFields;
        return obj;
      }, {});
    },
    shippingRows() {
      const set = new Set();
      for (const f of Object.values(this.printVariantCopy?.fulfiller || {})) {
        for (const cc of Object.keys(f.shippingCosts || {})) set.add(cc);
        for (const cc of Object.keys(f.express?.shipping || {})) set.add(cc);
      }
      set.delete("default");
      return ["default", ...[...set].sort()];
    },
    availableShippingCountries() {
      const used = new Set(this.shippingRows);
      return this.countryItems.filter((item) => !used.has(item.value));
    },
    rankRows() {
      const keys = Object.keys(this.printVariantCopy?.fulfillRank || {}).filter(
        (cc) => cc !== "default"
      );
      return ["default", ...keys.sort()];
    },
    availableRankCountries() {
      const used = new Set(Object.keys(this.printVariantCopy?.fulfillRank || {}));
      return this.countryItems.filter((item) => !used.has(item.value));
    },
    countryItems() {
      return Object.keys(countryCodes).map((cc) => ({
        value: cc,
        text: `${this.flag(cc)} ${cc}`,
      }));
    },
  },
  watch: {
    printVariant: {
      deep: true,
      handler: function (newVal) {
        if (newVal) {
          this.printVariantCopy = this.prepareCopy(newVal);
          this.loadPrices();
        }
      },
    },
  },
  created() {
    this.printVariantCopy = this.prepareCopy(this.printVariant || this.defaultPrintVariant);
    this.loadPrices();
  },
  methods: {
    ...mapActions(["addPrintVariant", "savePrintVariantFull", "setPrintVariantArchived"]),
    async toggleArchived() {
      // Nur das Archiv-Flag schreiben — ungespeicherte Preisänderungen im
      // Dialog bleiben davon unberührt
      await this.setPrintVariantArchived({
        key: this.printVariant.key,
        isArchived: !this.printVariant.isArchived,
      });
      this.closeDialog();
    },
    flag(cc) {
      if (!/^[A-Z]{2}$/.test(cc)) return "";
      return String.fromCodePoint(
        ...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
      );
    },
    fulfillerName(key) {
      return this.allFulfiller.find((f) => f.key === key)?.name || key;
    },
    /** Kopie mit garantierten Strukturen (fulfillRank.default, shipping-Maps) */
    prepareCopy(source) {
      const copy = _.cloneDeep(source);
      if (!copy.fulfillRank) copy.fulfillRank = {};
      if (!copy.fulfillRank.default) copy.fulfillRank.default = [];
      for (const f of Object.values(copy.fulfiller || {})) {
        if (!f.shippingCosts || typeof f.shippingCosts !== "object") f.shippingCosts = {};
        if (f.express && typeof f.express === "object") {
          // Alte Bug-Felder normalisieren (isAvailable ist die Wahrheit)
          delete f.express.available;
          delete f.express.shippingCosts;
          f.express.isAvailable = !!f.express.isAvailable;
          if (!f.express.shipping || typeof f.express.shipping !== "object") {
            f.express.shipping = {};
          }
        }
      }
      return copy;
    },
    onSelectMockup(id, orientation) {
      const field = orientation === "landscape" ? "mockupLandscape" : "mockupPortrait";
      this.$set(this.printVariantCopy, field, id);
    },
    // --- Preise ---
    expressAvailable(fk) {
      return !!this.printVariantCopy.fulfiller[fk]?.express?.isAvailable;
    },
    setExpressAvailable(fk, value) {
      const f = this.printVariantCopy.fulfiller[fk];
      if (!f.express || typeof f.express !== "object") {
        this.$set(f, "express", { isAvailable: !!value, costs: 0, shipping: {} });
      } else {
        this.$set(f.express, "isAvailable", !!value);
      }
    },
    setShipping(fk, kind, cc, value) {
      const f = this.printVariantCopy.fulfiller[fk];
      const table = kind === "express" ? f.express.shipping : f.shippingCosts;
      if (value === "" || value === null) {
        this.$delete(table, cc);
      } else {
        this.$set(table, cc, Number(value));
      }
    },
    addShippingCountry() {
      const cc = this.newShippingCountry;
      const first = this.configuredFulfiller[0];
      if (first) this.$set(this.printVariantCopy.fulfiller[first].shippingCosts, cc, 0);
      this.newShippingCountry = "";
    },
    removeShippingCountry(cc) {
      for (const f of Object.values(this.printVariantCopy.fulfiller)) {
        this.$delete(f.shippingCosts || {}, cc);
        if (f.express?.shipping) this.$delete(f.express.shipping, cc);
      }
    },
    // --- Aktiv-Markierung in der Versandtabelle ---
    /** Rang für ein Land — wie im Backend: Länder-Regel, sonst Default. */
    rankFor(cc) {
      const rank = this.printVariantCopy.fulfillRank;
      if (cc !== "default" && rank[cc]?.length) return rank[cc];
      return rank.default || [];
    },
    /** Effektiver Versandpreis wie ihn das Backend zahlen würde. */
    effectiveShipping(fk, kind, cc) {
      const f = this.printVariantCopy.fulfiller[fk];
      const table = kind === "express" ? f?.express?.shipping : f?.shippingCosts;
      if (!table) return 0;
      const direct = cc === "default" ? undefined : table[cc];
      return direct ?? table.default ?? 0;
    },
    /** Gesamtkosten = Produktion + effektiver Versand (inkl. Fallback). */
    totalCosts(fk, cc) {
      const production = Number(this.printVariantCopy.fulfiller[fk]?.costs) || 0;
      return production + this.effectiveShipping(fk, "standard", cc);
    },
    formatPrice(value) {
      return value.toFixed(2).replace(".", ",") + " €";
    },
    /** Aktiver Fulfiller je Zelle: Standard = Rang[0]; Express = erster im
     *  Rang mit verfügbarem Express (Backend-Logik). */
    activeFulfiller(kind, cc) {
      const rank = this.rankFor(cc);
      if (kind === "standard") return rank[0];
      return rank.find((fk) => this.expressAvailable(fk));
    },
    cellClass(fk, kind, cc) {
      // Markierung bewusst nur für Standard-Versand (Express außen vor)
      if (kind === "express") return "";
      if (this.activeFulfiller(kind, cc) !== fk) return "";
      // Nur Fulfiller mit gepflegtem Preis (Land oder Fallback) vergleichen —
      // sonst gewinnen Fulfiller ohne Versanddaten mit fälschlichen 0 €
      const candidates = this.rankFor(cc).filter((key) => {
        const sc = this.printVariantCopy.fulfiller[key]?.shippingCosts || {};
        if (sc.default !== undefined) return true;
        return cc !== "default" && sc[cc] !== undefined;
      });
      // 0 € beim gewählten Fulfiller = fehlender/kaputter Versandpreis
      if (!this.effectiveShipping(fk, "standard", cc)) return "pv-cell-error";
      // Entscheidungsbasis: Gesamtkosten (Produktion + Versand)
      const own = this.totalCosts(fk, cc);
      const cheapest = candidates.length
        ? Math.min(...candidates.map((key) => this.totalCosts(key, cc)))
        : own;
      return own <= cheapest ? "pv-cell-best" : "pv-cell-pricier";
    },
    cellTitle(fk, kind, cc) {
      const cls = this.cellClass(fk, kind, cc);
      if (cls === "pv-cell-error") return "FEHLER: Kein Versandpreis gepflegt — Bestellungen laufen mit 0 € Versand in die Statistik";
      if (cls === "pv-cell-best") return "Aktiver Fulfiller — günstigste Gesamtkosten (Produktion + Versand)";
      if (cls === "pv-cell-pricier") return "Aktiver Fulfiller — eine Alternative im Rang wäre gesamt günstiger";
      return "";
    },
    // --- Fulfillment-Reihenfolge ---
    rankCandidates(cc) {
      const list = this.printVariantCopy.fulfillRank[cc] || [];
      return this.configuredFulfiller.filter((fk) => !list.includes(fk));
    },
    appendRank(cc, fk) {
      if (!fk) return;
      const list = this.printVariantCopy.fulfillRank[cc];
      if (!list.includes(fk)) list.push(fk);
    },
    moveRank(cc, index) {
      if (index === 0) return;
      const list = this.printVariantCopy.fulfillRank[cc];
      const [item] = list.splice(index, 1);
      list.splice(index - 1, 0, item);
    },
    removeRank(cc, index) {
      this.printVariantCopy.fulfillRank[cc].splice(index, 1);
    },
    addRankCountry() {
      this.$set(this.printVariantCopy.fulfillRank, this.newRankCountry, [
        ...this.printVariantCopy.fulfillRank.default,
      ]);
      this.newRankCountry = "";
    },
    removeRankCountry(cc) {
      this.$delete(this.printVariantCopy.fulfillRank, cc);
    },
    // --- Producer ---
    apiFieldKeys(fk) {
      const template = this.requiredFieldsByFulfiller[fk] || {};
      const actual = this.printVariantCopy.fulfiller[fk] || {};
      const keys = new Set([...Object.keys(template), ...Object.keys(actual)]);
      return [...keys].filter((k) => !CORE_FIELDS.includes(k) && k !== "key");
    },
    addFulfiller() {
      const fulfiller = this.allFulfiller.find((f) => f.key === this.selectedFulfiller);
      this.$set(this.printVariantCopy.fulfiller, fulfiller.key, _.cloneDeep(fulfiller.requiredFields));
      this.selectedFulfiller = "";
    },
    // --- Verkaufspreise (eigenes Dokument) ---
    async loadPrices() {
      this.shopifyPrices = {};
      this.countryPrices = {};
      this.marketPrices = {};
      this.manualPrices = {};
      this.manualPricesSaved = "{}";
      const key = this.printVariant && this.printVariant.key;
      if (!key) return;
      const snap = await getDoc(doc(db, "settings", "printVariantPrices"));
      const d = snap.exists() ? snap.data() : {};
      this.priceConfig = mergeConfig(d.config);
      // Grundländer + Länder mit Verkäufen (Shopify Markets: DK, SE, CZ …)
      this.priceCountries = [...new Set([...PRICE_COUNTRIES, ...Object.keys(d.countries || {})])];
      const entry = (d.variants || {})[key] || {};
      this.shopifyPrices = entry.shopify || {};
      this.countryPrices = entry.countryPrice || {};
      this.marketPrices = entry.marketPrice || {};
      const manual = {};
      for (const cc of PRICE_COUNTRIES) if (entry.manual && entry.manual[cc] !== undefined && entry.manual[cc] !== null) manual[cc] = entry.manual[cc];
      this.manualPrices = manual;
      this.manualPricesSaved = JSON.stringify(manual);
    },
    shopPriceFor(cc) {
      const entry = { shopify: this.shopifyPrices, countryPrice: this.countryPrices };
      const mp = this.marketPrices[cc];
      if (mp && Number(mp.price) > 0) return { price: Number(mp.price), currency: mp.currency, label: `Preisliste ${mp.market}` };
      const cp = this.countryPrices[cc];
      if (cp && Number(cp.price) > 0) return { price: Number(cp.price), currency: cp.currency, label: `Bestellungen ${cc}` };
      const key = shopFor(entry, cc, this.priceConfig);
      const s = this.shopifyPrices[key];
      return s ? { price: Number(s.price), currency: s.currency, label: key } : null;
    },
    async savePrices() {
      const key = this.printVariant && this.printVariant.key;
      if (!key) return;
      this.savingPrices = true;
      try {
        const manual = {};
        for (const [cc, v] of Object.entries(this.manualPrices)) if (v !== "" && v !== null && v !== undefined && Number(v) > 0) manual[cc] = Number(v);
        await setDoc(doc(db, "settings", "printVariantPrices"), { variants: { [key]: { manual } } }, { merge: true });
        this.manualPrices = manual;
        this.manualPricesSaved = JSON.stringify(manual);
      } finally {
        this.savingPrices = false;
      }
    },
    // --- Speichern ---
    async save() {
      this.isSaving = true;
      try {
        // Leere Default-Rangfolge nicht persistieren (Verhalten wie bisher)
        const data = _.cloneDeep(this.printVariantCopy);
        if (data.fulfillRank?.default && !data.fulfillRank.default.length) {
          delete data.fulfillRank.default;
        }
        if (this.printVariant) {
          await this.savePrintVariantFull({ key: this.printVariant.key, printVariant: data });
        } else {
          await this.addPrintVariant(data);
        }
        this.closeDialog();
      } finally {
        this.isSaving = false;
      }
    },
    closeDialog() {
      this.onClose();
      this.printVariantCopy = this.prepareCopy(this.defaultPrintVariant);
    },
  },
};
</script>

<style lang="scss" scoped>
.pv-dialog {
  border-radius: 16px;

  &__title {
    padding-bottom: 4px;
  }
}

.pv-section {
  background: #fafbfc;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 14px;
  padding: 18px 20px;
  margin-bottom: 16px;

  &__head {
    display: flex;
    align-items: center;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.2px;
    margin-bottom: 14px;
    color: rgba(0, 0, 0, 0.8);

    &--toggle {
      cursor: pointer;
      margin-bottom: 0;
      user-select: none;
    }
  }

  &--collapsible .pv-producer:first-of-type {
    margin-top: 14px;
  }
}

.pv-subhead {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 8px;

  &__hint {
    font-weight: 400;
    color: rgba(0, 0, 0, 0.45);
    margin-left: 8px;
  }
}

.pv-table-wrap {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 10px;
  overflow: auto;
}

.pv-table {
  width: 100%;
  border-collapse: collapse;

  th {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: rgba(0, 0, 0, 0.45);
    padding: 10px 12px 6px;
    text-align: left;
    white-space: nowrap;
  }

  td {
    padding: 4px 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
  }

  &__label {
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    padding-left: 12px !important;
  }

  &__input {
    min-width: 96px;
    max-width: 140px;

    &:not(.pv-cell-best):not(.pv-cell-pricier) ::v-deep .v-input__slot {
      background: #f4f5f7 !important;
    }
  }
}

.pv-fallback {
  background: rgba(63, 157, 104, 0.08);

  .pv-table__label {
    color: #2f7d52;
  }
}

.pv-prices {
  &__shops {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 8px;
  }
  &__shop {
    font-size: 11.5px;
    padding: 2px 9px;
    border-radius: 999px;
    background: #eef0f3;
  }
}
.pv-muted {
  color: rgba(0, 0, 0, 0.3);
}

/* Aktiver Fulfiller in der Versandtabelle */
.pv-cell-best {
  background: #e4f5eb;
  box-shadow: inset 3px 0 0 #3f9d68;

  ::v-deep input {
    font-weight: 700;
    color: #2f7d52;
  }
}
.pv-cell-error {
  background: #fde3e3;
  box-shadow: inset 3px 0 0 #d92c2c;

  ::v-deep input,
  ::v-deep input::placeholder {
    font-weight: 700;
    color: #b71c1c;
  }
}

.pv-cell-pricier {
  background: #fdeeda;
  box-shadow: inset 3px 0 0 #e59b3c;

  ::v-deep input {
    font-weight: 700;
    color: #a96617;
  }
}

.pv-total {
  font-size: 11px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.45);
  padding: 0 12px 4px;
  white-space: nowrap;
}

.pv-cell-best .pv-total {
  color: #2f7d52;
}

.pv-cell-pricier .pv-total {
  color: #a96617;
}

.pv-totals-toggle {
  margin-left: auto;

  ::v-deep .v-label {
    font-size: 12px;
  }
}

.pv-legend {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: 14px;
  font-weight: 400;
  font-size: 11.5px;
  color: rgba(0, 0, 0, 0.5);

  &__dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 3px;

    &--best {
      background: #3f9d68;
    }

    &--pricier {
      background: #e59b3c;
    }
  }
}

.pv-switch {
  margin: 0 auto;
  padding: 0;
  display: inline-block;
}

.pv-add-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;

  &__select {
    max-width: 220px;
  }
}

.pv-rank {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 10px;
  padding: 8px 12px;
  margin-bottom: 8px;

  &--default {
    background: rgba(63, 157, 104, 0.06);
    border-color: rgba(63, 157, 104, 0.25);

    .pv-rank__country {
      color: #2f7d52;
    }
  }

  &__country {
    font-size: 13px;
    font-weight: 700;
    min-width: 74px;
    display: inline-flex;
    align-items: center;
  }

  &__chips {
    display: inline-flex;
    gap: 6px;
    flex-wrap: wrap;
  }
}

.pv-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #eef0f3;
  border-radius: 999px;
  padding: 3px 10px 3px 4px;
  font-size: 12.5px;
  font-weight: 600;

  &__rank {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 999px;
    background: #fff;
    font-size: 11px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  }

  &__btn {
    cursor: pointer;
    opacity: 0.55;

    &:hover {
      opacity: 1;
    }
  }
}

.pv-chip-move,
.pv-chip-enter-active,
.pv-chip-leave-active {
  transition: all 0.2s ease;
}
.pv-chip-enter,
.pv-chip-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

.pv-producer {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 10px;

  &__name {
    font-size: 13px;
    font-weight: 700;
    margin-bottom: 10px;
  }
}

.pv-actions {
  padding: 12px 24px 20px;
}
</style>
